/**
 * AlertContext — Global real-time alert provider
 * ==============================================
 * One WebSocket. One source of truth. Every screen feeds from here.
 *
 * Why: Previously each screen called useRealTimeAlerts() independently,
 * spawning multiple WS connections and siloing state. Now App.tsx mounts
 * this provider once and every screen consumes the same live stream.
 */

import React, { createContext, useContext, useCallback, useState } from "react";
import { useRealTimeAlerts, type IntelToast, type AlertFeedStatus } from "@/hooks/useRealTimeAlerts";
import type { Alert } from "@/constants/mockData";

interface AlertContextValue {
  alerts: Alert[];
  heldAlerts: Alert[];
  heldSymbolCount: number | null;
  status: AlertFeedStatus;
  heldStatus: AlertFeedStatus;
  /**
   * Start the feed now. On web the provider waits for the first tap or key
   * (boot performance); a screen whose whole job is showing alerts calls this
   * on mount, or a deep link to it would sit on an empty list indefinitely.
   */
  activate: () => void;
  intelToasts: IntelToast[];
  newAlertCount: number;
  isConnected: boolean;
  dismissAlert: (id: string) => void;
  snoozeAlert: (id: string, durationMs?: number) => void;
  markAsRead: (id: string) => void;
  clearIntelToasts: () => void;
}

const AlertContext = createContext<AlertContextValue | null>(null);

export function AlertProvider({ children, enabled = true }: { children: React.ReactNode; enabled?: boolean }) {
  const [activated, setActivated] = useState(false);
  const { alerts, heldAlerts, heldSymbolCount, status, heldStatus, intelToasts, newAlertCount, isConnected, dismissAlert, snoozeAlert, markAsRead, clearIntelToasts } = useRealTimeAlerts([], enabled || activated);
  const activate = useCallback(() => setActivated(true), []);

  const value: AlertContextValue = {
    alerts,
    heldAlerts,
    heldSymbolCount,
    status,
    heldStatus,
    activate,
    intelToasts,
    newAlertCount,
    isConnected,
    dismissAlert,
    snoozeAlert,
    markAsRead,
    clearIntelToasts,
  };

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
}

export function useGlobalAlerts(): AlertContextValue {
  const ctx = useContext(AlertContext);
  if (!ctx) {
    throw new Error("useGlobalAlerts must be used inside <AlertProvider>");
  }
  return ctx;
}

export function useAlertsForSymbol(symbol: string): Alert[] {
  const { alerts } = useGlobalAlerts();
  return alerts.filter((a) => a.asset === symbol || a.symbol === symbol);
}
